import {Injectable} from '@angular/core';
import {HttpService} from '../core/http.service';
import {TtActivityType} from '../../interfaces/time-tracking/tt-activity-type';
import {map} from 'rxjs/operators';
import {TtActivityTypeFactory} from '../../factories/time-tracking/tt-activity-type-factory';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {TtActivityTypesTable} from '../../tables/time-tracking/tt-activity-types.table';
import {TtActivityEntry} from '../../interfaces/time-tracking/tt-activity-entry';
import {TtActivityEntriesTable} from '../../tables/time-tracking/tt-activity-entries.table';
import {UserService} from '../core/user.service';
import {DateTime} from 'luxon';
import {TtActivityEntryFactory} from '../../factories/time-tracking/tt-activity-entry-factory';
import {TtWorkingTimeTypeFactory} from '../../factories/time-tracking/tt-working-time-type-factory';
import {TtWorkingTimeTypesTable} from '../../tables/time-tracking/tt-working-time-types.table';
import {EmployeesTable} from '../../tables/core/employees.table';
import {LightEmployeeFactory} from '../../factories/employees/light-employee-factory';
import {LightUserFactory} from '../../factories/core/light-user-factory';
import {TtWorkingTimeEntriesServices} from './tt-working-time-entries.service';
import {TtProductEntry} from '../../interfaces/time-tracking/tt-product-entry';
import {TtProductEntriesTable} from '../../tables/time-tracking/tt-product-entries.table';
import {TimeoutHelper} from '../../helpers/timeout-helper';

interface TtActivityEntriesFormOptions {
  ttActivityTypes: TtActivityType[]
}

@Injectable({
  providedIn: 'root'
})
export class TtActivityEntriesService {

  /**
   *
   * @param httpService
   * @param ttActivityEntriesTable
   * @param ttWorkingTimeTypesTable
   * @param ttWorkingTimeEntriesService
   * @param employeesTable
   * @param ttProductEntriesTable
   * @param ttActivityTypesTable
   */
  constructor(private httpService: HttpService,
              private ttActivityEntriesTable: TtActivityEntriesTable,
              private ttWorkingTimeTypesTable: TtWorkingTimeTypesTable,
              private ttWorkingTimeEntriesService: TtWorkingTimeEntriesServices,
              private employeesTable: EmployeesTable,
              private ttProductEntriesTable: TtProductEntriesTable,
              private ttActivityTypesTable: TtActivityTypesTable) {

  }

  /**
   *
   */
  public async loadTtActivityEntries(): Promise<TtActivityEntry[]> {
    const ttActivityEntries = await firstValueFrom(this.httpService.get<any>({
      url: ['tt-activity-entries'],
      query: {
        employee_id: UserService.getUser().employee.id,
        date_from: DateTime.local().toISODate(),
        date_to: DateTime.local().toISODate()
      }
    }).pipe(map(_ttActivityEntries => {
      return TtActivityEntryFactory.fromArray(_ttActivityEntries);
    })));

    if(!await this.ttActivityEntriesTable.isSynced()) {
      throw new Error('TtActivityEntriesTable is not synced');
    }

    await this.ttActivityEntriesTable.set(ttActivityEntries);
    await this.ttWorkingTimeEntriesService.calculateTtWorkingTimeEntries();
    return ttActivityEntries;
  }

  /**
   *
   * @param ttActivityEntry
   * @param ttProductEntries
   */
  public async createTtActivityEntry(
    ttActivityEntry: TtActivityEntry,
    ttProductEntries: TtProductEntry[] = []
  ): Promise<TtActivityEntry> {
    const employee = await this.employeesTable.get(ttActivityEntry.employeeId);
    ttActivityEntry.employee = LightEmployeeFactory.fromEmployee(employee);
    ttActivityEntry.userId = UserService.getUser().id;
    ttActivityEntry.user = LightUserFactory.fromUser(UserService.getUser());
    const _ttActivityEntry =  await this.ttActivityEntriesTable.add(ttActivityEntry);

    if(ttProductEntries.length > 0) {
      ttProductEntries = this.mapUuidToTtProductEntries(_ttActivityEntry, ttProductEntries);
      await this.ttProductEntriesTable.addMany(ttProductEntries);
    }

    return _ttActivityEntry;
  }

  /**
   *
   * @param ttActivityEntry
   * @param ttProductEntries
   */
  public async updateTtActivityEntry(ttActivityEntry: TtActivityEntry,
                                     ttProductEntries: TtProductEntry[] = []): Promise<TtActivityEntry> {
    const employee = await this.employeesTable.get(ttActivityEntry.employeeId);
    ttActivityEntry.employee = LightEmployeeFactory.fromEmployee(employee);
    ttActivityEntry.userId = UserService.getUser().id;
    ttActivityEntry.user = LightUserFactory.fromUser(UserService.getUser());
    const _ttActivityEntry = await this.ttActivityEntriesTable.update(ttActivityEntry);

    ttProductEntries = this.mapUuidToTtProductEntries(_ttActivityEntry, ttProductEntries);
    await this.ttProductEntriesTable.addOrUpdateManyByTtActivityEntryUuid(_ttActivityEntry.uuid, ttProductEntries);

    return _ttActivityEntry;
  }

  /**
   *
   * @param ttActivityEntryUuid
   * @param withoutSync
   */
  public async deleteTtActivityEntry(ttActivityEntryUuid: string, withoutSync: boolean = false): Promise<void> {
    await this.ttActivityEntriesTable.remove(ttActivityEntryUuid, withoutSync);
    await this.ttProductEntriesTable.removeByTtActivityEntryUuid(ttActivityEntryUuid, withoutSync);
    await this.ttWorkingTimeEntriesService.calculateTtWorkingTimeEntries();
  }

  /**
   *
   * @param query
   */
  public async loadTtActivityEntryFormOptions(query: {
    serviceFacilityId?: number,
    serviceId?: number
  } = {}): Promise<TtActivityEntriesFormOptions> {
    const _query: any = {};

    if(query.serviceFacilityId) {
      _query.service_facility_id = query.serviceFacilityId;
    }

    if(query.serviceId) {
      _query.service_id = query.serviceId;
    }

    const ttActivityEntriesFormOption =  await lastValueFrom(this.httpService.get<{
      tt_activity_types: any[],
      tt_working_time_types: any[]
    }>({
      url: ['time-tracking', 'activity-entry-form-options'],
      query: _query
    }).pipe(map(_ttActivityEntriesFormOptions => {
      return {
        ttActivityTypes: TtActivityTypeFactory.fromArray(_ttActivityEntriesFormOptions.tt_activity_types),
        ttWorkingTimeTypes: TtWorkingTimeTypeFactory.fromArray(_ttActivityEntriesFormOptions.tt_working_time_types)
      };
    })));

    await TimeoutHelper.sleep(50);
    await this.ttActivityTypesTable.set(ttActivityEntriesFormOption.ttActivityTypes);
    await TimeoutHelper.sleep(50);
    await this.ttWorkingTimeTypesTable.set(ttActivityEntriesFormOption.ttWorkingTimeTypes);

    return ttActivityEntriesFormOption;
  }

  /**
   *
   * @param ttActivityEntry
   * @param ttProductEntries
   * @private
   */
  private mapUuidToTtProductEntries(ttActivityEntry: TtActivityEntry, ttProductEntries: TtProductEntry[]): TtProductEntry[] {
    return ttProductEntries.map(_ttProductEntry => {
      _ttProductEntry.ttActivityEntryUuid = ttActivityEntry.uuid;
      _ttProductEntry.customer = ttActivityEntry.customer;
      _ttProductEntry.customerId = ttActivityEntry.customerId;
      _ttProductEntry.customerServiceFacilityId = ttActivityEntry.customer?.serviceFacilityId;
      _ttProductEntry.employee = ttActivityEntry.employee;
      _ttProductEntry.employeeId = ttActivityEntry.employeeId;
      _ttProductEntry.employeeServiceFacilityId = ttActivityEntry.employee.serviceFacilityId;
      _ttProductEntry.userId = ttActivityEntry.userId;
      _ttProductEntry.user = ttActivityEntry.user;
      _ttProductEntry.date = DateTime.fromISO(ttActivityEntry.timeFrom).toISODate();
      return _ttProductEntry;
    });
  }

}

